import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  getExtractionJobs, getExtractionJob, submitExtraction,
  reprocessExtractionJob, nudgeExtractionJob, resendExtractionCallback, deleteExtractionJob,
} from '../services/api';

/**
 * Extraction tab — award fund-authorization extraction jobs
 * (ORDS → POST /api/awards/extract → faculty names / purpose / exclusions → callback).
 *
 * List of jobs (auto-refreshing) with an expandable detail panel per job:
 *   Results  — one row per award with faculty names, purpose, cannot_be_used_for, per-row error
 *   Usage    — model, tokens, cost, timing, callback delivery
 *   History  — state timeline
 * Actions: Reprocess (re-run everything), Nudge (re-run only failed / missing rows),
 * Resend callback, DEV delete. A small "Submit JSON" panel lets you paste a payload for testing.
 */

const STATUS_COLORS = { RECEIVED: 'received', PROCESSING: 'processing', COMPLETE: 'completed', FAILED: 'failed' };
const STATUS_LABELS = { RECEIVED: 'Queued', PROCESSING: 'Extracting', COMPLETE: 'Complete', FAILED: 'Failed' };
const ACTIVE = ['RECEIVED', 'PROCESSING'];

function fmtDateTime(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function fmtMs(ms) {
  if (ms == null) return null;
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${ms} ms`;
}

function errMsg(e) { return e?.response?.data?.error || e?.message || 'Request failed'; }

const btnStyle = (color, bg, disabled) => ({
  padding: '3px 12px', borderRadius: 4, fontSize: 11, fontWeight: 600,
  border: `1px solid ${color}`, background: bg, color,
  cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1,
});
const smallBtn = (color, bg, disabled) => ({ ...btnStyle(color, bg, disabled), padding: '2px 8px', fontSize: 10 });

// ─── Detail panel ─────────────────────────────────────────────────────────────

function JobDetail({ jobId, onChanged }) {
  const [view, setView]       = useState('results');
  const [job, setJob]         = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [msg, setMsg]         = useState(null);           // { type: 'info'|'warn'|'error', text }
  const [busy, setBusy]       = useState(null);           // 'reprocess' | 'nudge' | 'resend'
  const [filter, setFilter]   = useState('all');          // 'all' | 'errors' | 'faculty'
  const [search, setSearch]   = useState('');
  const [jsonMode, setJsonMode] = useState('callback');   // 'callback' | 'full'
  const [copied, setCopied]   = useState(false);

  const load = useCallback(() => getExtractionJob(jobId, true)
    .then(d => { setJob(d); setError(null); return d; })
    .catch(e => setError(errMsg(e))), [jobId]);

  useEffect(() => { setLoading(true); load().finally(() => setLoading(false)); }, [load]);

  // Poll while the job is active
  useEffect(() => {
    if (!job || !ACTIVE.includes(job.status)) return;
    const id = setInterval(() => load().then(d => { if (d && !ACTIVE.includes(d.status)) onChanged?.(); }), 3000);
    return () => clearInterval(id);
  }, [job, load, onChanged]);

  async function run(kind, fn, okText) {
    setBusy(kind); setMsg(null);
    try {
      const r = await fn(jobId);
      setMsg({ type: 'info', text: okText(r) });
      await load();
      onChanged?.();
    } catch (e) {
      setMsg({ type: 'error', text: errMsg(e) });
    } finally {
      setBusy(null);
    }
  }

  const rowsById = new Map((job?.rows || []).map(r => [r.row_id, r]));
  const results  = (job?.results || []).map((r, i) => ({ ...r, _row: rowsById.get(i) || job?.rows?.[i] }));
  const okCount  = results.filter(r => !r.error).length;
  const errCount = results.filter(r => r.error).length;
  const facCount = results.filter(r => (r.faculty_names || []).length).length;
  const isActive = job && ACTIVE.includes(job.status);
  const canNudge = job && (job.status === 'PROCESSING' || (job.row_count - okCount) > 0);
  const canRerun = job && !isActive;

  // JSON views: the exact body POSTed to ORDS on completion, or the full GET response.
  const CALLBACK_KEYS = ['job_id', 'batch_id', 'source_system', 'status', 'model', 'row_count', 'processed_count', 'failed_count', 'processing_time_ms', 'error_message', 'results'];
  const callbackPayload = job ? Object.fromEntries(CALLBACK_KEYS.map(k => [k, job[k] ?? null])) : null;
  const fullResponse    = job ? (({ rows, ...rest }) => rest)(job) : null;   // GET was called with include_rows=true; hide the echo
  const jsonText = JSON.stringify(jsonMode === 'callback' ? callbackPayload : fullResponse, null, 2);

  function copyJson() {
    navigator.clipboard.writeText(jsonText).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  }
  function downloadJson() {
    const blob = new Blob([jsonText], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `${job.job_id}_${jsonMode}.json`; a.click();
    URL.revokeObjectURL(url);
  }

  const shown = results.filter(r => {
    if (filter === 'errors'  && !r.error) return false;
    if (filter === 'faculty' && !(r.faculty_names || []).length) return false;
    if (search) {
      const hay = `${r.award_number} ${r.faculty_name} ${r.purpose} ${r.cannot_be_used_for} ${r._row?.award_fund_authorization_description || ''}`.toLowerCase();
      if (!hay.includes(search.toLowerCase())) return false;
    }
    return true;
  });

  return (
    <div style={{ padding: '0 0 12px 0', background: 'var(--bg-card)', borderTop: '1px solid var(--border)' }}>
      {/* Sub-tabs + actions */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {['results', 'json', 'usage', 'history'].map(v => (
          <button key={v} onClick={() => setView(v)} style={{
            padding: '6px 16px', fontSize: 11, fontWeight: 600, border: 'none', cursor: 'pointer',
            background: view === v ? 'var(--bg)' : 'transparent',
            color: view === v ? 'var(--accent-hover)' : 'var(--text-muted)',
            borderBottom: view === v ? '2px solid var(--accent-hover)' : '2px solid transparent',
          }}>
            {v === 'results' ? `Award Results${job ? ` (${results.length}/${job.row_count})` : ''}`
              : v === 'json' ? '{ } JSON Response'
              : v === 'usage' ? 'Usage' : 'State History'}
          </button>
        ))}
        <div style={{ marginLeft: 'auto', padding: '4px 12px', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {job && (
            <>
              {canNudge && (
                <button disabled={!!busy} title="Re-run only the rows without a successful result; keeps everything already extracted"
                  style={btnStyle('#f59e0b', 'rgba(245,158,11,0.1)', !!busy)}
                  onClick={() => run('nudge', nudgeExtractionJob, r => r.message || 'Nudged')}>
                  {busy === 'nudge' ? '⟳ Nudging…' : `⚡ Nudge${job.row_count - okCount > 0 ? ` (${job.row_count - okCount})` : ''}`}
                </button>
              )}
              {canRerun && (
                <button disabled={!!busy} title="Re-run extraction for every row (clears existing results)"
                  style={btnStyle('var(--orange)', 'rgba(251,146,60,0.1)', !!busy)}
                  onClick={() => run('reprocess', reprocessExtractionJob, () => 'Job requeued — all rows will be extracted again')}>
                  {busy === 'reprocess' ? '⟳ Starting…' : '↺ Reprocess'}
                </button>
              )}
              {canRerun && (
                <button disabled={!!busy} title={job.callback?.url ? `Resend the full result set to ${job.callback.url}` : 'No callback URL configured (AWARD_EXTRACT_CALLBACK_URL)'}
                  style={btnStyle('var(--accent-hover)', 'rgba(99,102,241,0.1)', !!busy)}
                  onClick={() => run('resend', resendExtractionCallback, r => r.skipped ? 'No callback configured — nothing sent' : r.sent ? `Callback delivered (HTTP ${r.status})` : `Callback failed: ${r.error}`)}>
                  {busy === 'resend' ? '⟳ Sending…' : '↗ Resend Callback'}
                </button>
              )}
              <button className="detail-toggle-btn" style={{ fontSize: 10, padding: '3px 10px' }} onClick={() => load()} title="Refresh">⟳ Refresh</button>
            </>
          )}
          {msg && (
            <span style={{ fontSize: 10, width: '100%', textAlign: 'right',
              color: msg.type === 'error' ? 'var(--red)' : msg.type === 'warn' ? '#f59e0b' : 'var(--text-muted)' }}>
              {msg.text}
            </span>
          )}
        </div>
      </div>

      {loading && <div className="processing-indicator" style={{ padding: 16 }}><div className="spinner" /><span>Loading job…</span></div>}
      {error && <div className="error-box" style={{ margin: '0 12px', padding: '8px 12px', fontSize: 12 }}>{error}</div>}

      {/* Results */}
      {job && view === 'results' && (
        <div style={{ padding: '0 12px' }}>
          <div style={{ display: 'flex', gap: 12, fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span>{okCount} extracted · <span style={{ color: errCount ? 'var(--red)' : undefined }}>{errCount} with errors</span> · {facCount} with faculty</span>
            {isActive && <span style={{ color: 'var(--orange)' }}>⟳ {job.status === 'RECEIVED' ? 'queued' : 'extracting'} — refreshing every 3 s</span>}
            {job.error_message && <span style={{ color: 'var(--red)' }}>{job.error_message}</span>}
            <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
              <select className="state-select" value={filter} onChange={e => setFilter(e.target.value)} style={{ padding: '2px 6px', fontSize: 11 }} aria-label="Filter rows">
                <option value="all">All rows</option>
                <option value="errors">Errors only</option>
                <option value="faculty">With faculty</option>
              </select>
              <input className="state-select" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search award / name / text…" style={{ padding: '2px 8px', fontSize: 11, width: 220 }} aria-label="Search rows" />
            </span>
          </div>

          {results.length === 0 ? (
            <div className="empty-state" style={{ padding: 16 }}>
              {isActive ? 'No results yet — the worker is processing this job.' : 'No results stored for this job.'}
            </div>
          ) : (
            <div className="data-table-wrap">
              <table className="data-table compact">
                <thead>
                  <tr>
                    <th style={{ width: 30 }}>#</th>
                    <th>Award</th>
                    <th>Faculty name(s)</th>
                    <th>Purpose</th>
                    <th>Cannot be used for</th>
                    <th>Fund authorization (input)</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r, i) => (
                    <tr key={`${r.award_number}-${i}`} style={r.error ? { background: 'rgba(239,68,68,0.06)' } : undefined}>
                      <td className="mono" style={{ color: 'var(--text-muted)', fontSize: 10 }}>{(r.row_id ?? i) + 1}</td>
                      <td className="mono" style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {r.award_number}
                        {r.error && <div style={{ fontSize: 10, color: 'var(--red)', fontWeight: 500, whiteSpace: 'normal', maxWidth: 180 }} title={r.error}>⚠ {r.error}</div>}
                      </td>
                      <td style={{ minWidth: 140 }}>
                        {(r.faculty_names || []).length
                          ? r.faculty_names.map((n, j) => <span key={j} className="field-tag" style={{ marginRight: 4, marginBottom: 2, display: 'inline-block' }}>{n}</span>)
                          : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </td>
                      <td style={{ fontSize: 12, maxWidth: 280, whiteSpace: 'pre-wrap' }}>{r.purpose || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                      <td style={{ fontSize: 12, maxWidth: 240, whiteSpace: 'pre-wrap', color: r.cannot_be_used_for ? 'var(--orange)' : undefined }}>
                        {r.cannot_be_used_for || <span style={{ color: 'var(--text-muted)' }}>none stated</span>}
                      </td>
                      <td style={{ fontSize: 11, color: 'var(--text-muted)', maxWidth: 320, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                        title={r._row?.award_fund_authorization_description || ''}>
                        {r._row?.award_fund_authorization_description
                          ? (r._row.award_fund_authorization_description.length > 260
                              ? r._row.award_fund_authorization_description.slice(0, 260) + '…'
                              : r._row.award_fund_authorization_description)
                          : <em>empty</em>}
                      </td>
                    </tr>
                  ))}
                  {shown.length === 0 && (
                    <tr><td colSpan={6} className="empty-state" style={{ padding: 16, textAlign: 'center' }}>No rows match the filter</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* JSON response */}
      {job && view === 'json' && (
        <div style={{ padding: '0 12px' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
              {[['callback', 'Callback payload (sent to ORDS)'], ['full', 'Full GET response']].map(([m, label]) => (
                <button key={m} onClick={() => setJsonMode(m)} style={{
                  padding: '4px 12px', fontSize: 11, fontWeight: 600, border: 'none', cursor: 'pointer',
                  background: jsonMode === m ? 'var(--accent)' : 'transparent',
                  color: jsonMode === m ? '#fff' : 'var(--text-muted)',
                }}>{label}</button>
              ))}
            </div>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              {jsonMode === 'callback'
                ? `POST ${job.callback?.url || '<AWARD_EXTRACT_CALLBACK_URL>'} · headers job_id, job_status`
                : `GET /api/awards/extract/${job.job_id}`}
              {isActive && ' · job still running — results are partial'}
            </span>
            <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              <button className="detail-toggle-btn" style={{ fontSize: 10, padding: '3px 10px' }} onClick={copyJson}>{copied ? '✓ Copied' : '⧉ Copy'}</button>
              <button className="detail-toggle-btn" style={{ fontSize: 10, padding: '3px 10px' }} onClick={downloadJson}>⤓ Download</button>
            </span>
          </div>
          <pre className="json-viewer" style={{ margin: 0, fontSize: 11, maxHeight: 'calc(100vh - 260px)' }}>{jsonText}</pre>
        </div>
      )}

      {/* Usage */}
      {job && view === 'usage' && (() => {
        const lu = job.llm_usage;
        const cb = job.callback;
        const KV = ({ label, value }) => value == null ? null : (
          <div style={{ display: 'flex', gap: 8, fontSize: 12, padding: '3px 0', borderBottom: '1px solid var(--border)' }}>
            <span style={{ color: 'var(--text-muted)', minWidth: 170 }}>{label}</span>
            <span className="mono" style={{ fontWeight: 600, wordBreak: 'break-all' }}>{value}</span>
          </div>
        );
        const Section = ({ title, children }) => (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>{title}</div>
            {children}
          </div>
        );
        return (
          <div style={{ padding: '8px 16px 0', maxWidth: 560 }}>
            <Section title="General">
              <KV label="Job ID"          value={job.job_id} />
              <KV label="Batch reference" value={job.batch_id} />
              <KV label="Source system"   value={job.source_system} />
              <KV label="Status"          value={STATUS_LABELS[job.status] || job.status} />
              <KV label="Rows"            value={`${job.processed_count ?? 0} processed / ${job.row_count} total · ${job.failed_count ?? 0} failed`} />
              <KV label="Processing time" value={fmtMs(job.processing_time_ms)} />
              <KV label="Received"        value={fmtDateTime(job.createdAt)} />
              <KV label="Last update"     value={fmtDateTime(job.updatedAt)} />
            </Section>
            {lu ? (
              <Section title="LLM usage">
                <KV label="Model"             value={lu.model} />
                <KV label="Endpoint"          value={lu.litellm_base_url} />
                <KV label="Model calls"       value={lu.llm_calls} />
                <KV label="Prompt tokens"     value={lu.prompt_tokens?.toLocaleString()} />
                <KV label="Completion tokens" value={lu.completion_tokens?.toLocaleString()} />
                <KV label="Total tokens"      value={lu.total_tokens?.toLocaleString()} />
                {lu.total_cost_usd != null ? (
                  <>
                    <KV label="Prompt cost"     value={`$${lu.prompt_cost_usd?.toFixed(6)}`} />
                    <KV label="Completion cost" value={`$${lu.completion_cost_usd?.toFixed(6)}`} />
                    <div style={{ display: 'flex', gap: 8, fontSize: 12, padding: '5px 0', borderBottom: '2px solid var(--border)' }}>
                      <span style={{ color: 'var(--text-muted)', minWidth: 170, fontWeight: 700 }}>Total cost</span>
                      <span className="mono" style={{ fontWeight: 800, color: 'var(--green)', fontSize: 13 }}>
                        ${lu.total_cost_usd?.toFixed(6)} <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--text-muted)' }}>USD</span>
                      </span>
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>
                    No price on file for this model — add it to LLM_PRICING_OVERRIDES to see cost.
                  </div>
                )}
              </Section>
            ) : (
              <Section title="LLM usage">
                <div className="empty-state" style={{ padding: 8 }}>{isActive ? 'Recorded when the job completes.' : 'No usage recorded.'}</div>
              </Section>
            )}
            <Section title="Callback delivery">
              {cb?.url ? (
                <>
                  <KV label="URL"       value={cb.url} />
                  <KV label="Auth"      value={cb.auth} />
                  <KV label="Delivered" value={cb.sent ? `yes (HTTP ${cb.status ?? '—'})` : 'no'} />
                  <KV label="Attempts"  value={cb.attempts} />
                  <KV label="Last sent" value={cb.sent_at ? fmtDateTime(cb.sent_at) : null} />
                  {cb.error && <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 4 }}>Last error: {cb.error}</div>}
                </>
              ) : (
                <div className="empty-state" style={{ padding: 8 }}>No callback configured — ORDS polls GET /api/awards/extract/{'{job_id}'}.</div>
              )}
            </Section>
          </div>
        );
      })()}

      {/* History */}
      {job && view === 'history' && (
        <div style={{ padding: '0 16px' }}>
          {job.error_message && (
            <div style={{ margin: '0 0 10px', padding: '8px 12px', borderRadius: 6, background: 'var(--red-bg)', borderLeft: '3px solid var(--red)', color: 'var(--red)', fontSize: 12 }}>
              <strong>Error:</strong> {job.error_message}
            </div>
          )}
          {(job.state_history || []).map((h, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
              <div style={{
                marginTop: 3, width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                background: STATUS_COLORS[h.status] === 'failed' ? 'var(--red)' : STATUS_COLORS[h.status] === 'completed' ? 'var(--green)'
                          : STATUS_COLORS[h.status] === 'processing' ? 'var(--orange)' : 'var(--blue)',
              }} />
              <div>
                <span style={{ fontSize: 11, fontWeight: 600, marginRight: 6 }}>{STATUS_LABELS[h.status] || h.status}</span>
                <span style={{ fontSize: 10, color: 'var(--text-muted)', marginRight: 6 }}>{h.timestamp ? new Date(h.timestamp).toLocaleString() : ''}</span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{h.message}</span>
              </div>
            </div>
          ))}
          {(!job.state_history || job.state_history.length === 0) && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>No history available</span>}
        </div>
      )}
    </div>
  );
}

// ─── Submit panel (paste JSON for testing) ────────────────────────────────────

const SAMPLE = `{
  "batch_id": "UI-TEST-001",
  "rows": [
    { "award_number": "AWD-001", "award_fund_authorization_description": "Gift to support Prof. Jane Doe's robotics lab. Not to be used for salaries or equipment over $5,000." },
    { "award_number": "AWD-002", "award_fund_authorization_description": "General departmental support for Mechanical Engineering — no specific PI." }
  ]
}`;

function SubmitPanel({ onSubmitted }) {
  const [open, setOpen]   = useState(false);
  const [text, setText]   = useState(SAMPLE);
  const [busy, setBusy]   = useState(false);
  const [msg, setMsg]     = useState(null);

  async function submit() {
    setBusy(true); setMsg(null);
    let payload;
    try { payload = JSON.parse(text); } catch (e) { setMsg({ type: 'error', text: `Invalid JSON: ${e.message}` }); setBusy(false); return; }
    try {
      const r = await submitExtraction(payload);
      setMsg({ type: 'info', text: `Accepted ${r.row_count} row(s) as ${r.job_id}` });
      onSubmitted?.(r.job_id);
    } catch (e) {
      setMsg({ type: 'error', text: errMsg(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" style={{ padding: '10px 14px', marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button className="detail-toggle-btn" onClick={() => setOpen(o => !o)} style={{ fontSize: 11 }}>
          {open ? '▼' : '▶'} Submit JSON for extraction
        </button>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          Same payload ORDS sends to POST /api/awards/extract — see docs/samples/award_extraction_sample_25.json
        </span>
      </div>
      {open && (
        <div style={{ marginTop: 10 }}>
          <textarea className="submit-editor" value={text} onChange={e => setText(e.target.value)} rows={10} spellCheck={false}
            style={{ width: '100%', fontFamily: 'monospace', fontSize: 11, boxSizing: 'border-box' }} aria-label="Extraction payload JSON" />
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 6 }}>
            <button className="btn btn-primary" disabled={busy} onClick={submit} style={{ fontSize: 12, padding: '5px 14px' }}>
              {busy ? 'Submitting…' : 'Submit'}
            </button>
            {msg && <span style={{ fontSize: 11, color: msg.type === 'error' ? 'var(--red)' : 'var(--text-muted)' }}>{msg.text}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── List ─────────────────────────────────────────────────────────────────────

export default function ExtractionList() {
  const [jobs, setJobs]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [pageSize, setPageSize]   = useState(20);
  const [statusFilter, setStatus] = useState('');
  const [batchFilter, setBatchF]  = useState('');
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [expanded, setExpanded]   = useState(null);
  const [rowBusy, setRowBusy]     = useState({});   // job_id → 'reprocess' | 'nudge' | 'delete'
  const [rowErr, setRowErr]       = useState({});
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const batchInputRef = useRef(null);

  const load = useCallback(() => getExtractionJobs(page, pageSize, statusFilter, batchFilter)
    .then(d => { setJobs(d.jobs || []); setTotal(d.total || 0); setError(null); })
    .catch(e => setError(errMsg(e))), [page, pageSize, statusFilter, batchFilter]);

  useEffect(() => { setPage(1); }, [statusFilter, pageSize, batchFilter]);
  useEffect(() => { setLoading(true); load().finally(() => setLoading(false)); }, [load]);
  useEffect(() => { const id = setInterval(load, 5000); return () => clearInterval(id); }, [load]);

  async function rowAction(jobId, kind, fn, e) {
    e.stopPropagation();
    if (kind === 'delete' && !window.confirm(`Delete extraction job ${jobId}? (DEV_MODE only)`)) return;
    setRowBusy(b => ({ ...b, [jobId]: kind })); setRowErr(x => ({ ...x, [jobId]: null }));
    try {
      await fn(jobId);
      if (kind === 'delete' && expanded === jobId) setExpanded(null);
      await load();
    } catch (err) {
      setRowErr(x => ({ ...x, [jobId]: errMsg(err) }));
    } finally {
      setRowBusy(b => ({ ...b, [jobId]: null }));
    }
  }

  const activeCount = jobs.filter(j => ACTIVE.includes(j.status)).length;

  return (
    <div className="request-list-panel">
      <SubmitPanel onSubmitted={id => { load().then(() => setExpanded(id)); }} />

      <div className="list-toolbar">
        <div className="toolbar-left">
          <select className="state-select" value={statusFilter} onChange={e => setStatus(e.target.value)} aria-label="Filter by status">
            <option value="">All statuses</option>
            <option value="RECEIVED">Queued</option>
            <option value="PROCESSING">Extracting</option>
            <option value="COMPLETE">Complete</option>
            <option value="FAILED">Failed</option>
          </select>
          <input ref={batchInputRef} className="state-select" placeholder="batch_id filter" defaultValue={batchFilter}
            onKeyDown={e => { if (e.key === 'Enter') setBatchF(e.currentTarget.value.trim()); }}
            onBlur={e => setBatchF(e.currentTarget.value.trim())}
            style={{ width: 150, padding: '3px 8px' }} aria-label="Filter by batch id" />
          <span className="total-count">{total} job{total !== 1 ? 's' : ''}{activeCount ? ` · ${activeCount} active` : ''}</span>
        </div>
        <div className="toolbar-right">
          <select className="state-select" value={pageSize} onChange={e => setPageSize(Number(e.target.value))} aria-label="Rows per page" style={{ width: 'auto', padding: '3px 6px' }}>
            {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
          </select>
          <div className="pagination">
            <button className="page-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)} aria-label="Previous page">Prev</button>
            <span className="page-info" aria-live="polite" aria-atomic="true">{page}/{pages}</span>
            <button className="page-btn" disabled={page >= pages} onClick={() => setPage(p => p + 1)} aria-label="Next page">Next</button>
          </div>
        </div>
      </div>

      {error && <div className="error-box" style={{ padding: '8px 12px', fontSize: 12, marginBottom: 8 }}>{error}</div>}
      {loading && jobs.length === 0 && <div className="processing-indicator" style={{ padding: 16 }}><div className="spinner" /><span>Loading jobs…</span></div>}

      <div className="data-table-wrap">
        <table className="data-table compact">
          <thead>
            <tr>
              <th></th>
              <th>Job</th>
              <th>Batch ref</th>
              <th>Status</th>
              <th>Rows</th>
              <th>Model</th>
              <th>Tokens</th>
              <th>Cost</th>
              <th>Callback</th>
              <th>Received</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map(j => {
              const isExpanded = expanded === j.job_id;
              const busy       = rowBusy[j.job_id];
              const okRows     = (j.processed_count ?? 0) - (j.failed_count ?? 0);
              const pendingRows = j.row_count - okRows;
              const canRerun   = !ACTIVE.includes(j.status);
              const canNudge   = j.status === 'PROCESSING' || (canRerun && pendingRows > 0);
              return (
                <React.Fragment key={j.job_id}>
                  <tr style={{ cursor: 'pointer' }} onClick={() => setExpanded(prev => prev === j.job_id ? null : j.job_id)}>
                    <td style={{ width: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 10 }}>{isExpanded ? '▼' : '▶'}</td>
                    <td className="mono" style={{ fontSize: 11 }} title={j.job_id}>{j.job_id.replace(/^awx_/, '').slice(0, 8)}…</td>
                    <td className="mono" style={{ fontSize: 11 }}>{j.batch_id || '—'}</td>
                    <td>
                      <span className={`badge ${STATUS_COLORS[j.status] || 'received'}`}>{STATUS_LABELS[j.status] || j.status}</span>
                      {j.failed_count > 0 && <span title={`${j.failed_count} row(s) with errors`} style={{ marginLeft: 5, fontSize: 10, color: '#f59e0b', fontWeight: 700 }}>⚠ {j.failed_count}</span>}
                    </td>
                    <td style={{ fontSize: 11, textAlign: 'right' }}>
                      {ACTIVE.includes(j.status) || pendingRows > 0
                        ? <span style={{ color: pendingRows > 0 && canRerun ? '#f59e0b' : 'var(--text-muted)', fontWeight: pendingRows > 0 && canRerun ? 700 : 400 }}>{okRows}/{j.row_count}</span>
                        : <span style={{ color: 'var(--text-muted)' }}>{j.row_count}</span>}
                    </td>
                    <td className="mono" style={{ fontSize: 11 }}>{j.model || '—'}</td>
                    <td className="mono" style={{ fontSize: 11, textAlign: 'right' }}>{j.llm_usage?.total_tokens != null ? j.llm_usage.total_tokens.toLocaleString() : '—'}</td>
                    <td className="mono" style={{ fontSize: 11, textAlign: 'right', color: 'var(--green)' }}>{j.llm_usage?.total_cost_usd != null ? `$${j.llm_usage.total_cost_usd.toFixed(4)}` : '—'}</td>
                    <td style={{ fontSize: 11 }}>
                      {j.callback_sent ? <span style={{ color: 'var(--green)' }}>✓ sent</span>
                        : j.callback_error ? <span style={{ color: 'var(--red)' }} title={j.callback_error}>✗ failed</span>
                        : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    <td className="mono" style={{ fontSize: 11, whiteSpace: 'nowrap' }} title={j.createdAt}>{fmtDateTime(j.createdAt)}</td>
                    <td onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                      {canNudge && (
                        <button disabled={!!busy} title="Re-run only failed / missing rows" style={smallBtn('#f59e0b', 'rgba(245,158,11,0.1)', !!busy)}
                          onClick={e => rowAction(j.job_id, 'nudge', nudgeExtractionJob, e)}>{busy === 'nudge' ? '⟳ …' : '⚡ Nudge'}</button>
                      )}
                      {canRerun && (
                        <button disabled={!!busy} title="Re-run every row" style={{ ...smallBtn('#ef4444', 'rgba(239,68,68,0.1)', !!busy), marginLeft: 6 }}
                          onClick={e => rowAction(j.job_id, 'reprocess', reprocessExtractionJob, e)}>{busy === 'reprocess' ? 'Starting…' : 'Reprocess'}</button>
                      )}
                      <button disabled={!!busy} title="DEV: delete this job" style={{ ...smallBtn('#fbbf24', 'rgba(251,191,36,0.1)', !!busy), marginLeft: 6 }}
                        onClick={e => rowAction(j.job_id, 'delete', deleteExtractionJob, e)}>{busy === 'delete' ? '…' : 'DEV: Del'}</button>
                      {rowErr[j.job_id] && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--red)' }}>{rowErr[j.job_id]}</span>}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={11} style={{ padding: 0 }}>
                        <JobDetail jobId={j.job_id} onChanged={load} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {!loading && jobs.length === 0 && (
              <tr><td colSpan={11} className="empty-state" style={{ padding: 24, textAlign: 'center' }}>No extraction jobs yet — ORDS posts to /api/awards/extract, or use “Submit JSON” above.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
